/**
 * @alfe.ai/openclaw-sync — OpenClaw Sync plugin.
 *
 * Wraps the sync engine as a lifecycle-managed integration. Same shape as
 * @alfe.ai/openclaw-memory-cloud / -secrets / -google: one AgentApiClient
 * is constructed in `activate()` and reused for every API call. The agent's
 * identity is resolved server-side from the API key — the plugin never
 * touches `/auth/validate`, never plumbs an `agentId` around, never writes
 * a `.alfesync/` directory.
 *
 * Lifecycle:
 *   - activate(api): construct client, start sync engine + watcher
 *   - deactivate(api): stop watcher, drop relay connections, clean up
 *   - configure(api, config): swap schedule/scope at runtime
 *
 * Registers gateway RPC methods: `sync.now`, `sync.status`.
 */

import { createRequire } from 'node:module';
import {
  resolveConfig as resolveAlfeConfig,
  configExists as alfeConfigExists,
  DEFAULT_SOCKET_PATH,
  DEFAULT_WORKSPACE_PATH,
  type ResolvedConfig as AlfeResolvedConfig,
} from '@alfe.ai/config';
import { AgentApiClient, type SyncAgentInfo } from '@alfe.ai/agent-api-client';

import { existsSync } from 'node:fs';
import { join } from 'node:path';

import { createSyncEngine, type SyncEngine, type SyncResult } from './sync-engine.js';
import { startWatcher } from './watcher.js';
import { createSharedSyncEngine, type SharedSyncEngine, type SharedScope } from './shared-sync.js';
import { createDeleteBrake, type DeleteBrake } from './delete-brake.js';
import { readManifest } from './manifest.js';

const require = createRequire(import.meta.url);
const pkg = require('../package.json') as { version: string };

// ── Constants ───────────────────────────────────────────────
const SYNC_CAPABILITIES = ['sync.push', 'sync.pull', 'sync.fullSync'] as const;
const SYNC_RELAY_RECONNECT_BASE_MS = 1000;
const SYNC_RELAY_RECONNECT_MAX_MS = 30000;
const SYNC_RELAY_DEBOUNCE_MS = 500;

// ── Types ───────────────────────────────────────────────────

interface PluginLogger {
  info(msg: string): void;
  warn(msg: string): void;
  error(msg: string): void;
  debug(msg: string): void;
}

interface PluginServiceContext {
  config: Record<string, unknown>;
  workspaceDir?: string;
  stateDir: string;
  logger: PluginLogger;
}

interface PluginApi {
  logger: PluginLogger;
  registrationMode?: 'full' | 'setup-only' | 'setup-runtime' | 'cli-metadata';
  config?: SyncPluginConfig;
  registerGatewayMethod?: (name: string, handler: () => Promise<unknown>) => void;
  registerService(service: {
    id: string;
    start: (ctx: PluginServiceContext) => void | Promise<void>;
    stop?: (ctx: PluginServiceContext) => void | Promise<void>;
  }): void;
}

interface IpcClient {
  on(event: string, handler: (...args: unknown[]) => void): void;
  request(method: string, params: unknown): Promise<{ ok: boolean; error?: { message: string } }>;
  start(): void;
  stop(): void;
}

interface SyncRelaySocket {
  on(event: string, handler: (...args: never[]) => void): void;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

interface RelayMessage {
  type: string;
  status?: string;
  agentId?: string;
  message?: string;
  filePath?: string;
  etag?: string;
  eventType?: string;
}

export interface SyncPluginConfig {
  /** Workspace path to sync. Defaults to the resolved Alfe workspace. */
  workspacePath?: string;
  /** Sync scope — which data categories to sync. */
  syncScope?: ('config' | 'conversations' | 'memory')[];
  /** Sync schedule — how often to auto-sync. */
  syncSchedule?: 'realtime' | 'hourly' | 'daily' | 'weekly';
  /** IPC socket path override. */
  socketPath?: string;
  /** Sync relay URL override (default: derived from apiUrl). */
  syncRelayUrl?: string;
  /** Enable shared file sync for org/team/project scopes. Default: true. */
  sharedSync?: boolean;
}

// ── Plugin State ────────────────────────────────────────────

let client: AgentApiClient | null = null;
let agentId: string | null = null;
let syncEngine: SyncEngine | null = null;
let sharedSyncEngine: SharedSyncEngine | null = null;
let stopWatcher: (() => Promise<void>) | null = null;
let deleteBrake: DeleteBrake | null = null;
let daemonIpcClient: IpcClient | null = null;
let scheduledInterval: ReturnType<typeof setInterval> | null = null;
let currentConfig: SyncPluginConfig = {};
let lastSyncResult: SyncResult | null = null;
let syncRelayWs: SyncRelaySocket | null = null;
let syncRelayReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let syncRelayReconnectAttempt = 0;
let syncRelayDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const syncRelayPendingPaths = new Map<
  string,
  { etag?: string; eventType: 'created' | 'deleted' }
>();

// ── Schedule Helpers ────────────────────────────────────────

const SCHEDULE_INTERVALS_MS: Record<string, number> = {
  hourly: 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
};

function clearSchedule() {
  if (scheduledInterval) {
    clearInterval(scheduledInterval);
    scheduledInterval = null;
  }
}

function setupSchedule(schedule: string, log: PluginLogger) {
  clearSchedule();
  if (schedule === 'realtime') {
    log.info('Sync schedule: realtime (file watcher active)');
    return;
  }

  const intervalMs = SCHEDULE_INTERVALS_MS[schedule];
  if (!intervalMs) {
    log.warn(`Unknown sync schedule: ${schedule}, defaulting to hourly`);
    setupSchedule('hourly', log);
    return;
  }

  log.info(`Sync schedule: ${schedule} (every ${String(intervalMs / 1000)}s)`);
  scheduledInterval = setInterval(() => {
    if (!syncEngine) return;
    const engine = syncEngine;
    void (async () => {
      try {
        log.info(`Scheduled sync (${schedule}) starting...`);
        lastSyncResult = await engine.fullSync({ quiet: true });
        log.info(
          `Scheduled sync complete: ${String(lastSyncResult.pushed)} pushed, ${String(lastSyncResult.pulled)} pulled`,
        );
      } catch (err: unknown) {
        log.error(`Scheduled sync failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    })();
  }, intervalMs);
  scheduledInterval.unref();
}

// ── Daemon IPC ──────────────────────────────────────────────

async function connectToDaemon(socketPath: string, log: PluginLogger): Promise<IpcClient | null> {
  try {
    const modPath = '@alfe.ai/openclaw';
    const mod = (await import(/* webpackIgnore: true */ modPath)) as {
      IPCClient: new (socketPath: string, log: PluginLogger) => IpcClient;
    };
    const ipc = new mod.IPCClient(socketPath, log);

    ipc.on('connected', () => {
      void (async () => {
        log.info('Connected to Alfe daemon — registering sync capabilities...');
        const response = await ipc.request('capability.register', {
          plugin: '@alfe.ai/openclaw-sync',
          capabilities: [...SYNC_CAPABILITIES],
        });
        if (response.ok) {
          log.info('Sync capabilities registered with daemon');
        } else {
          log.warn(`Failed to register sync capabilities: ${response.error?.message ?? 'unknown'}`);
        }
      })();
    });

    ipc.on('disconnected', (...args: unknown[]) => {
      const reason = typeof args[0] === 'string' ? args[0] : String(args[0]);
      log.warn(`Disconnected from Alfe daemon: ${reason}`);
    });

    ipc.on('message', (...args: unknown[]) => {
      const msg = args[0] as Record<string, unknown> | undefined;
      if (msg?.type === 'SYNC_NOW' || msg?.command === 'SYNC_NOW') {
        log.info('Received SYNC_NOW command — triggering immediate sync...');
        if (syncEngine) {
          const engine = syncEngine;
          void (async () => {
            try {
              lastSyncResult = await engine.fullSync({ quiet: true });
              log.info(
                `SYNC_NOW complete: ${String(lastSyncResult.pushed)} pushed, ${String(lastSyncResult.pulled)} pulled`,
              );
            } catch (err: unknown) {
              log.error(`SYNC_NOW failed: ${err instanceof Error ? err.message : String(err)}`);
            }
          })();
        }
      }

      if (msg?.type === 'SHARED_SCOPES') {
        const scopes = msg.scopes as SharedScope[] | undefined;
        const engine = sharedSyncEngine;
        if (scopes && engine) {
          log.info(`Received SHARED_SCOPES update: ${String(scopes.length)} scope(s)`);
          void (async () => {
            try {
              await engine.updateScopes(scopes);
            } catch (err: unknown) {
              log.error(
                `SHARED_SCOPES update failed: ${err instanceof Error ? err.message : String(err)}`,
              );
            }
          })();
        }
      }
    });

    ipc.on('error', (...args: unknown[]) => {
      const err = args[0];
      log.debug(`Daemon IPC error: ${err instanceof Error ? err.message : String(err)}`);
    });

    ipc.start();
    return ipc;
  } catch {
    log.info('Alfe daemon not available — Sync plugin running standalone');
    return null;
  }
}

// ── Sync Relay Connection ───────────────────────────────────

function clearSyncRelayReconnect() {
  if (syncRelayReconnectTimer) {
    clearTimeout(syncRelayReconnectTimer);
    syncRelayReconnectTimer = null;
  }
}

function clearSyncRelayDebounce() {
  if (syncRelayDebounceTimer) {
    clearTimeout(syncRelayDebounceTimer);
    syncRelayDebounceTimer = null;
  }
}

async function processPendingNotifications(log: PluginLogger) {
  if (syncRelayPendingPaths.size === 0) return;

  const entries = new Map(syncRelayPendingPaths);
  syncRelayPendingPaths.clear();

  const sharedEntries = new Map<string, { etag?: string; eventType: 'created' | 'deleted' }>();
  const privateEntries = new Map<string, { etag?: string; eventType: 'created' | 'deleted' }>();

  for (const [filePath, info] of entries) {
    if (filePath.startsWith('shared/')) {
      sharedEntries.set(filePath, info);
    } else {
      privateEntries.set(filePath, info);
    }
  }

  if (sharedEntries.size > 0 && sharedSyncEngine) {
    for (const [filePath, info] of sharedEntries) {
      try {
        await sharedSyncEngine.handleNotification(filePath, info.eventType);
      } catch (err: unknown) {
        log.error(
          `Shared sync relay: failed ${info.eventType} ${filePath}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  }

  if (privateEntries.size > 0 && syncEngine) {
    const engine = syncEngine;
    const toPull: string[] = [];
    const toDelete: string[] = [];

    for (const [filePath, info] of privateEntries) {
      if (info.eventType === 'deleted') {
        toDelete.push(filePath);
      } else {
        toPull.push(filePath);
      }
    }

    for (const filePath of toDelete) {
      try {
        await engine.removeLocalFile(filePath, { quiet: true });
        log.debug(`Sync relay: deleted ${filePath}`);
      } catch (err: unknown) {
        log.error(
          `Sync relay: failed to delete ${filePath}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }

    if (toPull.length > 0) {
      try {
        const result = await engine.pullFiles(toPull, { quiet: true });
        if (result.pulled > 0) log.info(`Sync relay: pulled ${String(result.pulled)} file(s)`);
        if (result.errors > 0) log.warn(`Sync relay: ${String(result.errors)} pull error(s)`);
      } catch (err: unknown) {
        log.error(`Sync relay: pull failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }
}

async function connectToSyncRelay(
  relayUrl: string,
  token: string,
  agentIdForSubscribe: string,
  log: PluginLogger,
): Promise<SyncRelaySocket | null> {
  try {
    const { default: WebSocket } = await import('ws');

    const wsUrl = `${relayUrl}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);

    ws.on('open', () => {
      log.info('Connected to Sync Relay');
      syncRelayReconnectAttempt = 0;
      ws.send(JSON.stringify({ type: 'SUBSCRIBE', agentId: agentIdForSubscribe }));
    });

    ws.on('message', (data: Buffer | string) => {
      let message: RelayMessage;
      try {
        message = JSON.parse(data.toString()) as RelayMessage;
      } catch {
        return;
      }

      switch (message.type) {
        case 'SUBSCRIBE_ACK':
          if (message.status === 'ok') {
            log.info(`Subscribed to sync notifications for agent ${message.agentId ?? agentIdForSubscribe}`);
            const sharedEngine = sharedSyncEngine;
            if (sharedEngine) {
              void (async () => {
                try {
                  await sharedEngine.fullSync();
                  log.info('Shared sync: reconnect full sync complete');
                } catch (err: unknown) {
                  log.error(
                    `Shared sync: reconnect full sync failed: ${err instanceof Error ? err.message : String(err)}`,
                  );
                }
              })();
            }
          } else {
            log.warn(`Sync relay subscribe failed: ${message.message ?? 'unknown'}`);
          }
          break;

        case 'FILE_CHANGED':
          if (message.filePath) {
            syncRelayPendingPaths.set(message.filePath, {
              etag: message.etag,
              eventType: message.eventType === 'deleted' ? 'deleted' : 'created',
            });
          }
          clearSyncRelayDebounce();
          syncRelayDebounceTimer = setTimeout(
            () => void processPendingNotifications(log),
            SYNC_RELAY_DEBOUNCE_MS,
          );
          syncRelayDebounceTimer.unref();
          break;

        case 'PING':
          try {
            ws.send(JSON.stringify({ type: 'PONG' }));
          } catch {
            // ignore
          }
          break;
      }
    });

    ws.on('close', (code: number) => {
      log.info(`Sync Relay disconnected (code=${String(code)})`);
      syncRelayWs = null;
      scheduleSyncRelayReconnect(relayUrl, token, agentIdForSubscribe, log);
    });

    ws.on('error', (err: Error) => {
      log.debug(`Sync Relay error: ${err.message}`);
    });

    return ws as SyncRelaySocket;
  } catch (err: unknown) {
    log.debug(`Failed to connect to Sync Relay: ${err instanceof Error ? err.message : String(err)}`);
    scheduleSyncRelayReconnect(relayUrl, token, agentIdForSubscribe, log);
    return null;
  }
}

function scheduleSyncRelayReconnect(
  relayUrl: string,
  token: string,
  agentIdForSubscribe: string,
  log: PluginLogger,
) {
  clearSyncRelayReconnect();
  const delay = Math.min(
    SYNC_RELAY_RECONNECT_BASE_MS * Math.pow(2, syncRelayReconnectAttempt),
    SYNC_RELAY_RECONNECT_MAX_MS,
  );
  syncRelayReconnectAttempt++;
  log.debug(
    `Reconnecting to Sync Relay in ${String(delay)}ms (attempt ${String(syncRelayReconnectAttempt)})`,
  );

  syncRelayReconnectTimer = setTimeout(() => {
    void (async () => {
      syncRelayWs = await connectToSyncRelay(relayUrl, token, agentIdForSubscribe, log);
    })();
  }, delay);
  syncRelayReconnectTimer.unref();
}

function disconnectSyncRelay() {
  clearSyncRelayReconnect();
  clearSyncRelayDebounce();
  syncRelayPendingPaths.clear();

  if (syncRelayWs) {
    try {
      syncRelayWs.send(JSON.stringify({ type: 'UNSUBSCRIBE' }));
      syncRelayWs.close(1000, 'Plugin deactivating');
    } catch {
      // ignore
    }
    syncRelayWs = null;
  }
}

function deriveRelayUrl(apiUrl: string): string {
  if (apiUrl.includes('dev.alfe.ai')) return 'wss://sync.dev.alfe.ai/ws';
  if (apiUrl.includes('demo.alfe.ai')) return 'wss://sync.demo.alfe.ai/ws';
  if (apiUrl.includes('test.alfe.ai')) return 'wss://sync.test.alfe.ai/ws';
  return 'wss://sync.alfe.ai/ws';
}

// ── Plugin Definition ───────────────────────────────────────

const plugin = {
  id: '@alfe.ai/openclaw-sync',
  name: 'Alfe Sync Plugin',
  description:
    'Back up agent configuration, conversations, and memory to the cloud with scheduled or real-time sync.',
  version: pkg.version,

  activate(api: PluginApi) {
    const log = api.logger;

    const pluginConfig: SyncPluginConfig = api.config ?? {};
    currentConfig = pluginConfig;

    let alfeConfig: { workspacePath: string; socketPath: string } | null = null;
    try {
      alfeConfig = resolveAlfeConfig();
    } catch {
      // alfe login hasn't run; logged below
    }

    const workspacePath =
      pluginConfig.workspacePath ?? alfeConfig?.workspacePath ?? DEFAULT_WORKSPACE_PATH;

    const syncScope = pluginConfig.syncScope ?? ['config', 'conversations', 'memory'];
    // Default to 'realtime' when no schedule is configured. The integration's
    // user-config schema marks sync_schedule as required, so an unset value
    // here means the framework didn't deliver the user's choice (manifest
    // template missing, applyConfig errored, etc.). 'daily' would leave a
    // managed agent silent for 24 hours after activation; 'realtime' fails
    // open into the safest mode — initial push runs immediately and the
    // watcher streams ongoing changes.
    const syncSchedule = pluginConfig.syncSchedule ?? 'realtime';
    const socketPath =
      pluginConfig.socketPath ?? alfeConfig?.socketPath ?? DEFAULT_SOCKET_PATH;

    const startSyncService = async () => {
      if ((globalThis as Record<string, unknown>).__alfeSyncPluginActivated === true) {
        log.debug('Alfe Sync plugin already activated — skipping duplicate');
        return;
      }
      (globalThis as Record<string, unknown>).__alfeSyncPluginActivated = true;
      log.info('Alfe Sync plugin activating...');
      log.info(`Sync scope: ${syncScope.join(', ')}`);
      log.info(`Sync schedule: ${syncSchedule}`);
      log.info(`Workspace: ${workspacePath}`);

      // Sync needs `~/.alfe/config.toml` (the existing `alfe login` config).
      // Identity (agentId / tenantId) is resolved server-side from the API
      // key on every AgentApiClient call.
      if (!alfeConfigExists()) {
        log.info('Sync skipped — no Alfe config found. Run `alfe login` to enable.');
        return;
      }

      let syncCfg: AlfeResolvedConfig;
      try {
        syncCfg = resolveAlfeConfig();
      } catch (err: unknown) {
        log.warn(
          `Sync skipped — failed to resolve credentials from ~/.alfe/config.toml: ${err instanceof Error ? err.message : String(err)}`,
        );
        return;
      }

      client = new AgentApiClient({ apiKey: syncCfg.apiKey, apiUrl: syncCfg.apiUrl });
      syncEngine = createSyncEngine({ workspacePath, client });
      log.info('Sync engine initialized');

      // Realtime mode → first-run reconcile of pre-existing files (chokidar
      // ignoreInitial means the watcher only emits for post-activation
      // changes; without this the workspace tree never reaches the cloud
      // unless a file is touched). This RECONCILES with cloud state rather
      // than blindly pushing: on a from-scratch VM rebuild the local
      // manifest is gone and `alfe setup` has just re-seeded the persona
      // templates, so a blind push(undefined) would upload those fresh seeds
      // OVER the agent's edited cloud copy and lose its edits. firstRunReconcile
      // restores cloud → local (cloud wins) when the cloud already holds
      // state, and only push-seeds files that are genuinely new locally. A
      // brand-new agent (empty cloud) still gets the push-only seed. See
      // sync-engine.ts firstRunReconcile for the fresh-vs-rebuild rationale.
      if (syncSchedule === 'realtime') {
        try {
          lastSyncResult = await syncEngine.firstRunReconcile({ quiet: true });
          log.info('Initial workspace reconcile complete');
        } catch (err: unknown) {
          log.warn(`Initial workspace reconcile failed: ${err instanceof Error ? err.message : String(err)}`);
        }
        deleteBrake = createDeleteBrake();
        try {
          stopWatcher = await startWatcher({
            workspacePath,
            debounceMs: 2000,
            onChanges: async (paths) => {
              if (!syncEngine) return;
              const existing: string[] = [];
              const missing: string[] = [];
              for (const p of paths) {
                if (existsSync(join(workspacePath, p))) existing.push(p);
                else missing.push(p);
              }
              log.debug(
                `Realtime sync: ${String(existing.length)} upload(s), ${String(missing.length)} delete(s)`,
              );
              if (existing.length > 0) {
                try {
                  lastSyncResult = await syncEngine.push(existing, { quiet: true });
                } catch (err: unknown) {
                  log.error(`Realtime push failed: ${err instanceof Error ? err.message : String(err)}`);
                }
              }
              if (missing.length > 0) {
                // Rolling-window rate brake: refuse the batch if cumulative
                // deletes in the last 60 s would exceed 30% of the manifest.
                // See packages/openclaw-sync/src/delete-brake.ts.
                const manifest = await readManifest(workspacePath);
                const manifestSize = Object.keys(manifest.files).length;
                if (deleteBrake && !deleteBrake.check(missing.length, manifestSize)) {
                  log.error(
                    `Sync delete brake tripped: ${String(deleteBrake.windowSum() + missing.length)} ` +
                      `deletes in last 60s vs manifest size ${String(manifestSize)} ` +
                      `(threshold 30%). Refusing batch — investigate the workspace state.`,
                  );
                } else {
                  try {
                    lastSyncResult = await syncEngine.pushDeletes(missing, { quiet: true });
                  } catch (err: unknown) {
                    log.error(`Realtime delete failed: ${err instanceof Error ? err.message : String(err)}`);
                  }
                }
              }
            },
          });
          log.info('File watcher started for realtime sync');
        } catch (err: unknown) {
          log.warn(`Failed to start file watcher: ${err instanceof Error ? err.message : String(err)}`);
        }
      } else {
        setupSchedule(syncSchedule, log);
      }

      // Daemon IPC (for SYNC_NOW + SHARED_SCOPES messages)
      daemonIpcClient = await connectToDaemon(socketPath, log);

      // Register with the sync service to materialize the SyncAgent record
      // and learn our own agentId for relay subscription.
      let registered: SyncAgentInfo | null = null;
      try {
        const result = await client.syncRegister();
        registered = result.agent;
        agentId = registered.agentId;
      } catch (err: unknown) {
        log.warn(`Sync register failed: ${err instanceof Error ? err.message : String(err)}`);
      }

      // Sync relay (real-time file change notifications)
      if (registered) {
        try {
          const relayUrl = pluginConfig.syncRelayUrl ?? deriveRelayUrl(syncCfg.apiUrl);
          syncRelayWs = await connectToSyncRelay(
            relayUrl,
            syncCfg.apiKey,
            registered.agentId,
            log,
          );
        } catch (err: unknown) {
          log.debug(
            `Sync Relay connection skipped: ${err instanceof Error ? err.message : String(err)}`,
          );
        }

        // Shared sync (org/team/project scopes pushed by gateway)
        if (pluginConfig.sharedSync !== false) {
          try {
            sharedSyncEngine = createSharedSyncEngine({ workspacePath, client }, log);
            log.info('Shared sync engine created — waiting for SHARED_SCOPES from gateway');
          } catch (err: unknown) {
            log.debug(
              `Shared sync engine skipped: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        }
      }
    };

    const stopSyncService = async () => {
      (globalThis as Record<string, unknown>).__alfeSyncPluginActivated = false;
      clearSchedule();
      disconnectSyncRelay();

      if (stopWatcher) {
        try {
          await stopWatcher();
          log.info('File watcher stopped');
        } catch (err: unknown) {
          log.debug(`Error stopping watcher: ${err instanceof Error ? err.message : String(err)}`);
        }
        stopWatcher = null;
      }
      deleteBrake = null;

      if (daemonIpcClient) {
        try {
          daemonIpcClient.stop();
          log.info('Disconnected from Alfe daemon');
        } catch (err: unknown) {
          log.debug(`Error disconnecting from daemon: ${err instanceof Error ? err.message : String(err)}`);
        }
        daemonIpcClient = null;
      }

      client = null;
      agentId = null;
      syncEngine = null;
      sharedSyncEngine = null;
      lastSyncResult = null;
      currentConfig = {};

      log.info('Alfe Sync plugin deactivated');
    };

    if (typeof api.registerGatewayMethod === 'function') {
      api.registerGatewayMethod('sync.now', async () => {
        if (!syncEngine) {
          return { ok: false, error: 'Sync engine not initialized — run `alfe login`' };
        }
        try {
          lastSyncResult = await syncEngine.fullSync({ quiet: true });
          return { ok: true, result: lastSyncResult };
        } catch (err: unknown) {
          return { ok: false, error: err instanceof Error ? err.message : String(err) };
        }
      });
      log.info('Registered gateway RPC method: sync.now');

      api.registerGatewayMethod('sync.status', () =>
        Promise.resolve({
          ok: true,
          initialized: !!syncEngine,
          agentId,
          schedule: currentConfig.syncSchedule ?? 'daily',
          scope: currentConfig.syncScope ?? ['config', 'conversations', 'memory'],
          lastResult: lastSyncResult,
          watcherActive: !!stopWatcher,
        }),
      );
      log.info('Registered gateway RPC method: sync.status');
    }

    api.registerService({
      id: 'alfe-sync-engine',
      start: () => startSyncService(),
      stop: () => stopSyncService(),
    });

    log.info('Alfe Sync plugin activated');
  },

  async deactivate(api: PluginApi) {
    (globalThis as Record<string, unknown>).__alfeSyncPluginActivated = false;
    const log = api.logger;
    log.info('Alfe Sync plugin deactivating...');

    clearSchedule();
    disconnectSyncRelay();

    if (stopWatcher) {
      try {
        await stopWatcher();
        log.info('File watcher stopped');
      } catch (err: unknown) {
        log.debug(`Error stopping watcher: ${err instanceof Error ? err.message : String(err)}`);
      }
      stopWatcher = null;
    }

    if (daemonIpcClient) {
      try {
        daemonIpcClient.stop();
        log.info('Disconnected from Alfe daemon');
      } catch (err: unknown) {
        log.debug(`Error disconnecting from daemon: ${err instanceof Error ? err.message : String(err)}`);
      }
      daemonIpcClient = null;
    }

    client = null;
    agentId = null;
    syncEngine = null;
    sharedSyncEngine = null;
    lastSyncResult = null;
    currentConfig = {};

    log.info('Alfe Sync plugin deactivated');
  },

  async configure(api: PluginApi, config: SyncPluginConfig) {
    const log = api.logger;
    log.info('Reconfiguring Alfe Sync plugin...');
    currentConfig = { ...currentConfig, ...config };

    if (config.syncSchedule) {
      if (config.syncSchedule === 'realtime' && syncEngine && !stopWatcher) {
        const workspacePath = currentConfig.workspacePath ?? syncEngine.workspacePath;
        stopWatcher = await startWatcher({
          workspacePath,
          debounceMs: 2000,
          onChanges: async (paths) => {
            if (!syncEngine) return;
            try {
              lastSyncResult = await syncEngine.push(paths, { quiet: true });
            } catch (err: unknown) {
              log.error(`Realtime push failed: ${err instanceof Error ? err.message : String(err)}`);
            }
          },
        });
        clearSchedule();
        log.info('Switched to realtime sync');
      } else if (config.syncSchedule !== 'realtime') {
        if (stopWatcher) {
          await stopWatcher();
          stopWatcher = null;
        }
        setupSchedule(config.syncSchedule, log);
      }
    }

    if (config.syncScope) {
      log.info(`Updated sync scope: ${config.syncScope.join(', ')}`);
    }

    log.info('Alfe Sync plugin reconfigured');
  },
};

export default plugin;
